import { registerLocaleData } from "@angular/common";
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { RouterOutlet } from "@angular/router";
import { TranslocoService } from "@jsverse/transloco";
import { arePropertiesDefined } from "@recursyve/nice-ts-utils";
import { defer, from, Observable } from "rxjs";
import { map } from "rxjs/operators";

import { I18nService } from "./providers/i18n.service";
import { AppService } from "./store/app.service";
import { AppStore } from "./store/app.store";

@Component({
    selector: "loc-app",
    imports: [RouterOutlet],
    templateUrl: "./app.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
    private readonly appService = inject(AppService);
    private readonly appStore = inject(AppStore);
    private readonly destroyRef = inject(DestroyRef);
    private readonly i18nService = inject(I18nService);
    private readonly matIconRegistry = inject(MatIconRegistry);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly translocoService = inject(TranslocoService);

    constructor() {
        effect(() => {
            const tenantBranding = this.appStore.tenantBranding();
            if (!arePropertiesDefined(tenantBranding, "faviconUrl", "name")) {
                return;
            }

            document.title = tenantBranding.name;
            document.getElementById("favicon")?.setAttribute("href", tenantBranding.faviconUrl);
        });
    }

    public ngOnInit(): void {
        this.matIconRegistry.setDefaultFontSetClass("material-icons-outlined");
        this.matIconRegistry.addSvgIconSetInNamespace(
            "heroicons_outline",
            this.sanitizer.bypassSecurityTrustResourceUrl("assets/icons/heroicons-outline.svg")
        );
        this.matIconRegistry.addSvgIconSetInNamespace(
            "heroicons_solid",
            this.sanitizer.bypassSecurityTrustResourceUrl("assets/icons/heroicons-solid.svg")
        );
        this.matIconRegistry.addSvgIconSetInNamespace(
            "streamline-bold",
            this.sanitizer.bypassSecurityTrustResourceUrl("assets/icons/bold-icons.svg")
        );
        this.matIconRegistry.addSvgIconSetInNamespace(
            "streamline-regular",
            this.sanitizer.bypassSecurityTrustResourceUrl("assets/icons/regular-icons.svg")
        );

        this.appService.loadServerState().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

        const language = this.i18nService.getDefaultLang();
        this.updateLanguage(language).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }

    private updateLanguage(lang: string): Observable<void> {
        return defer(() => {
            if (lang === "fr") {
                return from(import("@angular/common/locales/fr"));
            }

            return from(import("@angular/common/locales/en"));
        }).pipe(
            map((module) => {
                registerLocaleData(module.default);
                this.translocoService.setActiveLang(lang);
            })
        );
    }
}
